import { Component, ChangeDetectorRef, ViewChild, ElementRef, inject } from '@angular/core';
import { RegisterClass } from '@memberjunction/global';
import { BaseDashboard } from '@memberjunction/ng-shared';
import { ResourceData } from '@memberjunction/core-entities';
import { LogError, RunView } from '@memberjunction/core';

import {
    GraphQLOutboundActionClient,
    OutboundActionClient,
    OutboundReplyStats,
    OutboundSequence,
    OutboundSequenceDetail,
    OutboundEmailActivityRow,
    OutboundPersonaConfig,
    OutboundStepPerf,
    OutboundSequenceStep,
    OutboundStepVariant,
    OutboundStreamConfig,
    OutboundSyncResult,
    OutboundApolloList,
    OutboundCreateResult,
    OutboundUpdateResult,
    OutboundEnrollResult,
    OutboundRecommendation,
    OutboundVariantRollup,
    OutboundPoolMeeting,
    OutboundIngestResult,
} from '../../services/outbound-action.client';
import { LaunchResult } from '../../services/launch-brand-pipeline.client';

interface CompanyRow { ID: string; Name: string; }
interface CompanyIntegrationRow { ID: string; CompanyID: string; Integration: string; Configuration: string; }

/**
 * One person row in the Enroll-people editor. `domain` is intentionally absent
 * here — it is auto-derived from the email (substring after '@') when the inputs
 * are built, so the marketer never types it.
 */
interface EnrollRow {
    email: string;
    firstName: string;
    company: string;
    persona: string;
}

/**
 * Panel state machine shared by the stats and sequences panels.
 * 'not-deployed' is a first-class state (NOT an error): the Outbound
 * action records aren't synced to every environment yet, and the panel
 * must say so plainly instead of spraying console errors.
 */
type PanelStatus = 'idle' | 'loading' | 'ready' | 'empty' | 'not-deployed' | 'error';

interface PanelState {
    status: PanelStatus;
    message: string;
}

const IDLE: PanelState = { status: 'idle', message: '' };

/** Top-level shell tabs. The company selector stays global across all of them. */
type OutboundTab = 'overview' | 'sequences' | 'people' | 'build' | 'audiences' | 'bindings' | 'health';

interface OutboundTabDef {
    id: OutboundTab;
    label: string;
    icon: string;
}

/**
 * Outbound Dashboard — per-brand (MJ Companies) view over the Reply.io outbound actions.
 */
@Component({
    standalone: false,
    selector: 'obd-outbound-dashboard',
    templateUrl: './outbound-dashboard.component.html',
    styleUrls: ['../../styles/outbound-tokens.css', './outbound-dashboard.component.css'],
    providers: [
        GraphQLOutboundActionClient,
        { provide: OutboundActionClient, useExisting: GraphQLOutboundActionClient },
    ],
})
@RegisterClass(BaseDashboard, 'OutboundDashboard')
export class OutboundDashboardComponent extends BaseDashboard {
    private cdr = inject(ChangeDetectorRef);
    private actionClient = inject(OutboundActionClient);

    // ─── Shell tab state (company selector stays global across tabs) ───
    readonly tabs: readonly OutboundTabDef[] = [
        { id: 'overview',  label: 'Overview',  icon: 'fa-gauge-high' },
        { id: 'sequences', label: 'Sequences', icon: 'fa-list-ol' },
        { id: 'people',    label: 'People',    icon: 'fa-users' },
        { id: 'build',     label: 'Build',     icon: 'fa-wand-magic-sparkles' },
        // Re-homed Conduit (AIDP→Reply) screens — mounted as <obd-conduit> per tab.
        { id: 'audiences', label: 'Audiences', icon: 'fa-filter' },
        { id: 'bindings',  label: 'Bindings',  icon: 'fa-link' },
        { id: 'health',    label: 'Health',    icon: 'fa-heart-pulse' },
    ];
    activeTab: OutboundTab = 'overview';

    setTab(tab: OutboundTab): void {
        this.activeTab = tab;
    }

    // ─── Company (brand) state ─────────────────────────
    companies: CompanyRow[] = [];
    selectedCompanyId = '';
    isLoadingCompanies = true;
    companiesError = '';

    // ─── Panel state ───────────────────────────────────
    statsState: PanelState = IDLE;
    sequencesState: PanelState = IDLE;
    stats: OutboundReplyStats | null = null;
    sequences: OutboundSequence[] = [];

    // ─── Persona / config strip ───────────────────────
    persona: OutboundPersonaConfig | null = null;

    // ─── Sequence detail panel ────────────────────────
    @ViewChild('detailPanel') detailPanel?: ElementRef<HTMLElement>;
    selectedSequence: OutboundSequence | null = null;
    detailState: PanelState = IDLE;
    sequenceDetail: OutboundSequenceDetail | null = null;
    selectedStepIndex: number | null = null;

    // ─── Engagement (who opened/clicked/replied) for the selected sequence ──
    engagementState: PanelState = IDLE;
    engagement: OutboundEmailActivityRow[] = [];

    // ─── Streams (Apollo→Reply configs) ──────────────
    streams: OutboundStreamConfig[] = [];
    companyIntegrationId = '';

    // ─── Stream editor modal ──────────────────────────
    streamEditorVisible = false;
    editingStreamIndex = -1;  // -1 = new stream, ≥0 = editing existing
    formApolloListName = '';
    formPersonaName = '';
    formReplySequenceId = 0;
    /** Optional sending domain for this stream (#1071 P8). */
    formSendingDomain = '';
    /** Optional per-domain daily send cap; 0/blank = unset (#1071 P8). */
    formDailyDomainCap: number | null = null;
    isSavingStream = false;
    streamSaveError = '';
    apolloLists: OutboundApolloList[] = [];
    apolloListsLoading = false;

    // ─── Sync modal ───────────────────────────────────
    runPipelineVisible = false;
    isSyncing = false;
    syncResult: OutboundSyncResult | null = null;
    syncError = '';
    /** @deprecated kept for template compat; use isSyncing */
    isRunningPipeline = false;
    /** @deprecated kept for template compat; use syncResult */
    runPipelineResult = '';

    // ─── Launch Brand modal ───────────────────────────
    // SAFETY: PROVIDE-ONLY + no-send. This modal stages a brand's sequences
    // PAUSED-ready; it has no generate-copy, no enroll, and no start/unpause
    // affordance. DryRun defaults ON so the first click previews the plan.
    launchBrandVisible = false;

    // ─── Create-sequence modal (Gap 1) ────────────────
    createSequenceVisible = false;
    createCopyDocument = '';
    createNameOverride = '';
    isCreatingSequence = false;
    createError = '';
    createResult: OutboundCreateResult | null = null;

    // ─── Edit-copy modal (Gap 2) ──────────────────────
    editCopyVisible = false;
    editSequence: OutboundSequence | null = null;
    editCopyDocument = '';
    isEditingCopy = false;
    editError = '';
    /** Dry-run plan (wasDryRun=true) or live-apply result (wasDryRun=false). */
    editResult: OutboundUpdateResult | null = null;

    // ─── Inline per-variant copy editing ──────────────
    // SAFETY: 'Outbound: Update Sequence Variant' edits one variant's
    // subject+body in place; it never starts/unpauses/activates a sequence.
    /** Key of the variant currently being edited inline: `${stepIndex}:${variantIndex}`, or null. */
    editingVariantKey: string | null = null;
    variantEditSubject = '';
    variantEditBody = '';
    isSavingVariant = false;
    variantEditError = '';

    // ─── Enroll-review modal (Gap 3) ──────────────────
    // Marketer-friendly structured input — no raw JSON. Each row is one person;
    // the persona→sequence map is DERIVED from the personas actually used across
    // the rows (see buildEnrollInputs / enrollUsedPersonas), pre-seeded from the
    // brand's configured streams and overridable per persona.
    enrollReviewVisible = false;
    enrollRows: EnrollRow[] = [];
    /** Persona name → chosen Reply sequence id (0 = not yet chosen). Overridable. */
    enrollPersonaSeq: Record<string, number> = {};
    enrollAcknowledgeActive = false;
    isEnrolling = false;
    enrollError = '';
    enrollResult: OutboundEnrollResult | null = null;

    // ─── Recommendations panel (#1071 P8) ─────────────
    recommendationsState: PanelState = IDLE;
    recommendations: OutboundRecommendation[] = [];
    /** RecommendationID currently being applied/dismissed (disables its row buttons). */
    recBusyId: string | null = null;
    recActionError = '';

    // ─── Variant rollup sub-section (#1071 P8) ────────
    rollupExpanded = false;
    rollupState: PanelState = IDLE;
    variantRollup: OutboundVariantRollup | null = null;

    // ─── Central Pool panel (#1071 P8) ────────────────
    poolState: PanelState = IDLE;
    poolMeetings: OutboundPoolMeeting[] = [];
    /** Selected SourceBrand filter, '' = all. */
    poolBrandFilter = '';
    isIngesting = false;
    ingestError = '';
    ingestResult: OutboundIngestResult | null = null;

    /** Placeholder/help text for the markdown copy-doc editors — mirrors the parser's two formats. */
    readonly copyDocPlaceholder =
        'Paste the full markdown copy doc. Two formats are supported:\n\n' +
        '— Simple: frontmatter with "sequence_name:" then "## Step N" blocks ' +
        '(**Delay:** N days, **Subject options:** bullets, body).\n' +
        '— Stream/FINAL: H1 title, "## Sequence overview" table, "### Touch N — Day X" sections ' +
        'with the email body in a fenced code block.';

    /** Monotonic token so a stale company's late response can't clobber the current one. */
    private loadToken = 0;

    get selectedCompanyName(): string {
        return this.companies.find(c => c.ID === this.selectedCompanyId)?.Name ?? '';
    }

    get activeSequenceCount(): number {
        return this.sequences.filter(s => s.status === 'active').length;
    }

    // ─── BaseDashboard hooks ───────────────────────────
    protected override initDashboard(): void {
        this.NotifyLoadComplete();
    }

    protected override async loadData(): Promise<void> {
        await this.loadCompanies();
    }

    async GetResourceDisplayName(_data: ResourceData): Promise<string> {
        return 'Outbound';
    }

    // ─── Brands (MJ Companies) ─────────────────────────
    private async loadCompanies(): Promise<void> {
        this.isLoadingCompanies = true;
        this.companiesError = '';
        try {
            const rv = new RunView();
            const result = await rv.RunView<CompanyRow>({
                EntityName: 'MJ: Companies',
                Fields: ['ID', 'Name'],
                OrderBy: 'Name',
                MaxRows: 200,
                ResultType: 'simple',
            });
            if (!result.Success) {
                this.companiesError = result.ErrorMessage || 'Failed to load companies';
                LogError(`[OutboundDashboard] loadCompanies failed: ${this.companiesError}`);
            } else {
                this.companies = result.Results ?? [];
                if (!this.selectedCompanyId && this.companies.length > 0) {
                    this.selectedCompanyId = this.companies[0].ID;
                }
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            this.companiesError = msg;
            LogError(`[OutboundDashboard] loadCompanies threw: ${msg}`);
        }
        this.isLoadingCompanies = false;
        this.cdr.detectChanges();
        if (this.selectedCompanyId) {
            await Promise.all([
                this.loadOutboundData(),
                this.loadPersonaConfig(),
                this.loadRecommendations(),
                this.loadPool(),
            ]);
        }
    }

    onCompanyChange(): void {
        this.selectedSequence = null;
        this.sequenceDetail = null;
        this.detailState = IDLE;
        this.selectedStepIndex = null;
        this.rollupExpanded = false;
        this.rollupState = IDLE;
        this.variantRollup = null;
        this.poolBrandFilter = '';
        void Promise.all([
            this.loadOutboundData(),
            this.loadPersonaConfig(),
            this.loadRecommendations(),
            this.loadPool(),
        ]);
    }

    refresh(): void {
        void this.loadOutboundData();
    }

    async loadPersonaConfig(): Promise<void> {
        if (!this.selectedCompanyId) {
            this.persona = null;
            return;
        }
        try {
            const rv = new RunView();
            const result = await rv.RunView<CompanyIntegrationRow>({
                EntityName: 'MJ: Company Integrations',
                Fields: ['ID', 'CompanyID', 'Integration', 'Configuration'],
                ExtraFilter: `CompanyID = '${this.selectedCompanyId}'`,
                ResultType: 'simple',
            });
            if (!result.Success) {
                LogError(`[OutboundDashboard] loadPersonaConfig failed: ${result.ErrorMessage}`);
                this.persona = null;
                this.cdr.detectChanges();
                return;
            }
            const rows = result.Results ?? [];
            const replyRow = rows.find(r =>
                typeof r.Integration === 'string' &&
                r.Integration.toLowerCase().includes('reply')
            );
            if (!replyRow || !replyRow.Configuration) {
                this.persona = null;
                this.streams = [];
                this.cdr.detectChanges();
                return;
            }
            this.companyIntegrationId = replyRow.ID;
            const config: unknown = JSON.parse(replyRow.Configuration);
            if (
                typeof config === 'object' &&
                config !== null &&
                'personaName' in config &&
                'sendingDomain' in config &&
                typeof (config as Record<string, unknown>)['personaName'] === 'string' &&
                typeof (config as Record<string, unknown>)['sendingDomain'] === 'string'
            ) {
                const cfg = config as Record<string, unknown>;
                this.persona = {
                    personaName: cfg['personaName'] as string,
                    sendingDomain: cfg['sendingDomain'] as string,
                };
            } else {
                this.persona = null;
            }
            // Parse streams[] from config
            const rawStreams = (config as Record<string, unknown>)?.['streams'];
            if (Array.isArray(rawStreams)) {
                this.streams = rawStreams
                    .filter((s): s is Record<string, unknown> =>
                        typeof s === 'object' && s !== null &&
                        typeof (s as Record<string, unknown>)['apolloListName'] === 'string' &&
                        typeof (s as Record<string, unknown>)['personaName'] === 'string' &&
                        typeof (s as Record<string, unknown>)['replySequenceId'] === 'number'
                    )
                    .map((s): OutboundStreamConfig => ({
                        apolloListName: s['apolloListName'] as string,
                        personaName: s['personaName'] as string,
                        replySequenceId: s['replySequenceId'] as number,
                        ...(typeof s['sendingDomain'] === 'string' ? { sendingDomain: s['sendingDomain'] } : {}),
                        ...(typeof s['dailyDomainCap'] === 'number' ? { dailyDomainCap: s['dailyDomainCap'] } : {}),
                    }));
            } else {
                this.streams = [];
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[OutboundDashboard] loadPersonaConfig threw: ${msg}`);
            this.persona = null;
        }
        this.cdr.detectChanges();
    }

    onSequenceClick(seq: OutboundSequence): void {
        this.selectedSequence = seq;
        this.selectedStepIndex = null;
        this.detailState = { status: 'loading', message: '' };
        this.sequenceDetail = null;
        // Reset the per-sequence variant-rollup sub-section.
        this.rollupExpanded = false;
        this.rollupState = IDLE;
        this.variantRollup = null;
        this.cdr.detectChanges();
        // The detail panel renders full-width below the Stats/Sequences row; bring it
        // into view so selecting a sequence doesn't appear to "do nothing".
        this.detailPanel?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        this.loadEngagement(seq);
        void this.actionClient.getSequenceDetail(this.selectedCompanyId, seq.id).then(outcome => {
            switch (outcome.kind) {
                case 'success':
                    this.sequenceDetail = outcome.data;
                    this.detailState = outcome.data.steps.length > 0
                        ? { status: 'ready', message: '' }
                        : { status: 'empty', message: 'This sequence has no steps.' };
                    break;
                case 'not-deployed':
                    this.detailState = { status: 'not-deployed', message: outcome.message };
                    break;
                case 'error':
                    this.detailState = { status: 'error', message: outcome.message };
                    break;
            }
            this.cdr.detectChanges();
        });
    }

    closeDetail(): void {
        this.selectedSequence = null;
        this.sequenceDetail = null;
        this.detailState = IDLE;
        this.selectedStepIndex = null;
        this.rollupExpanded = false;
        this.rollupState = IDLE;
        this.variantRollup = null;
        this.engagementState = IDLE;
        this.engagement = [];
    }

    /**
     * Load per-contact engagement (who opened/clicked/replied) for one
     * sequence. Scoped server-side to the brand's registered sequences; here
     * we additionally narrow to the selected sequence. Guards against a stale
     * response when the user switches sequences before this resolves.
     */
    private loadEngagement(seq: OutboundSequence): void {
        const companyId = this.selectedCompanyId;
        this.engagementState = { status: 'loading', message: '' };
        this.engagement = [];
        void this.actionClient
            .getReplyEmailActivity(companyId, { sequenceIds: [seq.id], dateRangePreset: 'allTime', top: 200 })
            .then(outcome => {
                // Stale guard: the user may have selected a different sequence/company.
                if (companyId !== this.selectedCompanyId || this.selectedSequence?.id !== seq.id) return;
                switch (outcome.kind) {
                    case 'success':
                        this.engagement = outcome.data.rows;
                        this.engagementState = outcome.data.rows.length > 0
                            ? { status: 'ready', message: '' }
                            : { status: 'empty', message: 'No sends recorded for this sequence yet.' };
                        break;
                    case 'not-deployed':
                        this.engagementState = { status: 'not-deployed', message: outcome.message };
                        break;
                    case 'error':
                        this.engagementState = { status: 'error', message: outcome.message };
                        break;
                }
                this.cdr.detectChanges();
            });
    }

    /** Engagement rows where the contact opened at least once. */
    get openedContacts(): OutboundEmailActivityRow[] {
        return this.engagement.filter(r => r.isOpened);
    }

    /** Count of opened/clicked/replied across the loaded engagement rows. */
    get engagementSummary(): { opened: number; clicked: number; replied: number; total: number } {
        return {
            opened: this.engagement.filter(r => r.isOpened).length,
            clicked: this.engagement.filter(r => r.isClicked).length,
            replied: this.engagement.filter(r => r.isReplied).length,
            total: this.engagement.length,
        };
    }

    // ─── Launch Brand ──────────────────────────────────
    // SAFETY: provide-only + no-send. The modal stages a brand's sequences
    // PAUSED-ready (DryRun ON by default → plan first). It cannot generate copy,
    // enroll people, or start/unpause a sequence. We refresh the dashboard only
    // after a successful LIVE launch (the modal's `launched` output).

    openLaunchBrand(): void {
        this.launchBrandVisible = true;
        this.cdr.detectChanges();
    }

    closeLaunchBrand(): void {
        this.launchBrandVisible = false;
        this.cdr.detectChanges();
    }

    /** Fired after a successful LIVE launch (DryRun=false). Refresh the brand view. */
    onBrandLaunched(_result: LaunchResult): void {
        this.launchBrandVisible = false;
        // The launched brand may not be the currently-selected company; refresh
        // companies so a brand-new brand appears, then its sequences.
        void this.loadCompanies();
        this.cdr.detectChanges();
    }

    // ─── Create sequence (Gap 1) ───────────────────────
    // SAFETY: this path can only create a PAUSED sequence. There is no
    // start/unpause affordance anywhere in this UI — a human must start the
    // sequence manually from the Reply dashboard.

    openCreateSequence(): void {
        this.createCopyDocument = '';
        this.createNameOverride = '';
        this.createError = '';
        this.createResult = null;
        this.isCreatingSequence = false;
        this.createSequenceVisible = true;
        this.cdr.detectChanges();
    }

    closeCreateSequence(): void {
        if (this.isCreatingSequence) return;
        this.createSequenceVisible = false;
    }

    async submitCreateSequence(): Promise<void> {
        if (!this.selectedCompanyId || this.isCreatingSequence) return;
        if (!this.createCopyDocument.trim()) {
            this.createError = 'Paste a markdown copy doc first.';
            return;
        }
        this.isCreatingSequence = true;
        this.createError = '';
        this.createResult = null;
        this.cdr.detectChanges();

        const outcome = await this.actionClient.createSequence(
            this.selectedCompanyId,
            this.createCopyDocument,
            this.createNameOverride,
        );
        this.isCreatingSequence = false;
        switch (outcome.kind) {
            case 'success':
                this.createResult = outcome.data;
                void this.loadOutboundData(); // refresh the sequence list with the new (paused) sequence
                break;
            case 'not-deployed':
                this.createError = `Create action not deployed to this environment: ${outcome.message}`;
                break;
            case 'error':
                this.createError = outcome.message;
                break;
        }
        this.cdr.detectChanges();
    }

    // ─── Edit sequence copy (Gap 2) ─────────────────────
    // SAFETY: Update Reply Sequence only rewrites copy in place; it never
    // changes a sequence's run state. DryRun shows the plan with zero writes.

    openEditCopy(seq: OutboundSequence): void {
        this.editSequence = seq;
        this.editCopyDocument = '';
        this.editError = '';
        this.editResult = null;
        this.isEditingCopy = false;
        this.editCopyVisible = true;
        this.cdr.detectChanges();
    }

    closeEditCopy(): void {
        if (this.isEditingCopy) return;
        this.editCopyVisible = false;
    }

    async submitEditCopy(dryRun: boolean): Promise<void> {
        if (!this.selectedCompanyId || !this.editSequence || this.isEditingCopy) return;
        if (!this.editCopyDocument.trim()) {
            this.editError = 'Paste the revised markdown copy doc first.';
            return;
        }
        this.isEditingCopy = true;
        this.editError = '';
        this.editResult = null;
        this.cdr.detectChanges();

        const outcome = await this.actionClient.updateSequence(
            this.selectedCompanyId,
            this.editSequence.id,
            this.editCopyDocument,
            dryRun,
        );
        this.isEditingCopy = false;
        switch (outcome.kind) {
            case 'success':
                this.editResult = outcome.data;
                if (!dryRun) {
                    void this.loadOutboundData();
                }
                break;
            case 'not-deployed':
                this.editError = `Update action not deployed to this environment: ${outcome.message}`;
                break;
            case 'error':
                this.editError = outcome.message;
                break;
        }
        this.cdr.detectChanges();
    }

    // ─── Enroll people review (Gap 3) ───────────────────
    // SAFETY: enrolling into an ACTIVE sequence sends real email. Live enroll
    // is gated behind an explicit "acknowledge active sequence" checkbox; the
    // server-side action refuses an active sequence without it.

    openEnrollReview(): void {
        this.enrollRows = [this.blankEnrollRow()];
        this.enrollPersonaSeq = {};
        this.enrollAcknowledgeActive = false;
        this.enrollError = '';
        this.enrollResult = null;
        this.isEnrolling = false;
        this.enrollReviewVisible = true;
        this.cdr.detectChanges();
    }

    closeEnrollReview(): void {
        if (this.isEnrolling) return;
        this.enrollReviewVisible = false;
    }

    /** Distinct persona options for the row dropdown, drawn from configured streams. */
    get enrollPersonaOptions(): string[] {
        const seen = new Set<string>();
        const out: string[] = [];
        for (const s of this.streams) {
            const name = s.personaName?.trim();
            if (name && !seen.has(name)) {
                seen.add(name);
                out.push(name);
            }
        }
        return out;
    }

    /** True when the brand has zero configured streams → persona becomes free-text. */
    get enrollHasStreams(): boolean {
        return this.enrollPersonaOptions.length > 0;
    }

    /**
     * Distinct, non-empty personas actually used across the current rows — the
     * exact set the persona→sequence map must cover. Order follows first use.
     */
    get enrollUsedPersonas(): string[] {
        const seen = new Set<string>();
        const out: string[] = [];
        for (const row of this.enrollRows) {
            const persona = row.persona?.trim();
            if (persona && !seen.has(persona)) {
                seen.add(persona);
                out.push(persona);
            }
        }
        return out;
    }

    private blankEnrollRow(): EnrollRow {
        return { email: '', firstName: '', company: '', persona: '' };
    }

    addEnrollRow(): void {
        this.enrollRows = [...this.enrollRows, this.blankEnrollRow()];
        this.cdr.detectChanges();
    }

    removeEnrollRow(index: number): void {
        if (this.enrollRows.length <= 1) {
            // Keep at least one row so the editor is never empty — just clear it.
            this.enrollRows = [this.blankEnrollRow()];
        } else {
            this.enrollRows = this.enrollRows.filter((_, i) => i !== index);
        }
        this.syncEnrollPersonaSeq();
        this.cdr.detectChanges();
    }

    /** trackBy for the row editor — index is stable enough; rows have no id. */
    trackEnrollRow(index: number): number {
        return index;
    }

    /**
     * Called when a row's persona changes (or rows are removed). Seeds a default
     * sequence for any newly-used persona from the matching stream, and drops
     * map entries for personas no longer used. Existing user overrides are kept.
     */
    syncEnrollPersonaSeq(): void {
        const used = this.enrollUsedPersonas;
        const next: Record<string, number> = {};
        for (const persona of used) {
            if (this.enrollPersonaSeq[persona]) {
                next[persona] = this.enrollPersonaSeq[persona];
            } else {
                // Pre-select from the matching stream config when one exists.
                const stream = this.streams.find(s => s.personaName?.trim() === persona);
                next[persona] = stream?.replySequenceId ?? 0;
            }
        }
        this.enrollPersonaSeq = next;
    }

    /**
     * Validate the rows and build the action contract inputs. Returns null and
     * sets a plain-language enrollError on the first problem found.
     */
    private buildEnrollInputs(): {
        people: Array<{ email: string; firstName: string; company: string; domain: string; persona: string }>;
        map: Record<string, number>;
    } | null {
        const people: Array<{ email: string; firstName: string; company: string; domain: string; persona: string }> = [];
        for (const row of this.enrollRows) {
            const email = row.email.trim();
            if (!email) continue; // blank rows are ignored, not errors
            const persona = row.persona.trim();
            if (!persona) {
                this.enrollError = `Pick a persona for ${email}.`;
                return null;
            }
            const atIndex = email.lastIndexOf('@');
            const domain = atIndex >= 0 ? email.slice(atIndex + 1) : '';
            people.push({
                email,
                firstName: row.firstName.trim(),
                company: row.company.trim(),
                domain,
                persona,
            });
        }
        if (people.length === 0) {
            this.enrollError = 'Add at least one person with an email.';
            return null;
        }
        // Ensure the persona→sequence map is in sync with the rows, then validate.
        this.syncEnrollPersonaSeq();
        const map: Record<string, number> = {};
        for (const persona of this.enrollUsedPersonas) {
            const seqId = this.enrollPersonaSeq[persona];
            if (!Number.isInteger(seqId) || seqId < 1) {
                this.enrollError = `Pick a sequence for the '${persona}' persona.`;
                return null;
            }
            map[persona] = seqId;
        }
        return { people, map };
    }

    async submitEnroll(dryRun: boolean): Promise<void> {
        if (!this.selectedCompanyId || this.isEnrolling) return;
        const parsed = this.buildEnrollInputs();
        if (!parsed) {
            this.cdr.detectChanges();
            return;
        }
        // Guard: a live run must explicitly acknowledge active-sequence sends.
        if (!dryRun && !this.enrollAcknowledgeActive) {
            this.enrollError = 'Tick "Acknowledge active sequence" before running a live enroll — enrolling into an active sequence sends real email.';
            this.cdr.detectChanges();
            return;
        }
        this.isEnrolling = true;
        this.enrollError = '';
        this.enrollResult = null;
        this.cdr.detectChanges();

        const outcome = await this.actionClient.enrollPeople(
            this.selectedCompanyId,
            parsed.people,
            parsed.map,
            dryRun,
            !dryRun && this.enrollAcknowledgeActive,
        );
        this.isEnrolling = false;
        switch (outcome.kind) {
            case 'success':
                this.enrollResult = outcome.data;
                break;
            case 'not-deployed':
                this.enrollError = `Enroll action not deployed to this environment: ${outcome.message}`;
                break;
            case 'error':
                this.enrollError = outcome.message;
                break;
        }
        this.cdr.detectChanges();
    }

    // ─── Stream editor ─────────────────────────────────

    openAddStream(): void {
        this.editingStreamIndex = -1;
        this.formApolloListName = '';
        this.formPersonaName = '';
        this.formReplySequenceId = 0;
        this.formSendingDomain = '';
        this.formDailyDomainCap = null;
        this.streamSaveError = '';
        this.streamEditorVisible = true;
        this.cdr.detectChanges();
        void this.loadApolloLists();
    }

    openEditStream(index: number): void {
        const s = this.streams[index];
        if (!s) return;
        this.editingStreamIndex = index;
        this.formApolloListName = s.apolloListName;
        this.formPersonaName = s.personaName;
        this.formReplySequenceId = s.replySequenceId;
        this.formSendingDomain = s.sendingDomain ?? '';
        this.formDailyDomainCap = typeof s.dailyDomainCap === 'number' ? s.dailyDomainCap : null;
        this.streamSaveError = '';
        this.streamEditorVisible = true;
        this.cdr.detectChanges();
        void this.loadApolloLists();
    }

    closeStreamEditor(): void {
        if (this.isSavingStream) return;
        this.streamEditorVisible = false;
    }

    deleteStream(index: number): void {
        const s = this.streams[index];
        if (!s) return;
        if (!confirm(`Remove stream "${s.apolloListName} → ${s.personaName}"?`)) return;
        const updated = this.streams.filter((_, i) => i !== index);
        void this.persistStreams(updated);
    }

    async saveStream(): Promise<void> {
        if (!this.formApolloListName || !this.formPersonaName || !this.formReplySequenceId) {
            this.streamSaveError = 'All fields are required.';
            return;
        }
        const entry: OutboundStreamConfig = {
            apolloListName: this.formApolloListName,
            personaName: this.formPersonaName,
            replySequenceId: this.formReplySequenceId,
        };
        // Only persist the optional fields when set, so we never write empty
        // strings / zeros that would override a server-side default.
        const sendingDomain = this.formSendingDomain.trim();
        if (sendingDomain.length > 0) entry.sendingDomain = sendingDomain;
        if (typeof this.formDailyDomainCap === 'number' && this.formDailyDomainCap > 0) {
            entry.dailyDomainCap = this.formDailyDomainCap;
        }
        const updated = [...this.streams];
        if (this.editingStreamIndex >= 0) {
            updated[this.editingStreamIndex] = entry;
        } else {
            updated.push(entry);
        }
        await this.persistStreams(updated);
    }

    private async loadApolloLists(): Promise<void> {
        if (!this.selectedCompanyId) return;
        this.apolloListsLoading = true;
        this.cdr.detectChanges();
        const outcome = await this.actionClient.getApolloLists(this.selectedCompanyId);
        this.apolloListsLoading = false;
        if (outcome.kind === 'success') {
            this.apolloLists = outcome.data.filter(l => l.kind === 'contacts').sort((a, b) => a.name.localeCompare(b.name));
        } else {
            this.apolloLists = [];
        }
        this.cdr.detectChanges();
    }

    private async persistStreams(newStreams: OutboundStreamConfig[]): Promise<void> {
        if (!this.companyIntegrationId) {
            this.streamSaveError = 'No Reply CompanyIntegration record found for this company.';
            return;
        }
        this.isSavingStream = true;
        this.streamSaveError = '';
        this.cdr.detectChanges();
        try {
            // The gated 'Outbound: Save Stream Config' action does the read-merge-write
            // server-side: it resolves the CompanyIntegration from the authorized
            // CompanyID and merges streams into Configuration without clobbering the
            // other fields (email accounts, schedule, etc.). No client-side re-fetch.
            const saved = await this.actionClient.saveStreamConfig(this.selectedCompanyId, newStreams);
            if (!saved.success) throw new Error(saved.message);

            this.streams = newStreams;
            this.streamEditorVisible = false;
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[OutboundDashboard] persistStreams failed: ${msg}`);
            this.streamSaveError = msg;
        }
        this.isSavingStream = false;
        this.cdr.detectChanges();
    }

    openRunPipeline(): void {
        this.runPipelineVisible = true;
        this.syncResult = null;
        this.syncError = '';
    }

    closeRunPipeline(): void {
        if (this.isSyncing) return;
        this.runPipelineVisible = false;
    }

    async runSync(dryRun: boolean): Promise<void> {
        if (!this.selectedCompanyId || this.isSyncing) return;
        this.isSyncing = true;
        this.syncResult = null;
        this.syncError = '';
        this.cdr.detectChanges();
        const outcome = await this.actionClient.syncStreams(this.selectedCompanyId, dryRun);
        this.isSyncing = false;
        switch (outcome.kind) {
            case 'success':
                this.syncResult = outcome.data;
                break;
            case 'not-deployed':
                this.syncError = `Sync action not deployed to this environment: ${outcome.message}`;
                break;
            case 'error':
                this.syncError = outcome.message;
                break;
        }
        if (!dryRun && outcome.kind === 'success') {
            void this.loadOutboundData(); // refresh stats after live sync
        }
        this.cdr.detectChanges();
    }

    sequenceNameFor(sequenceId: number): string {
        return this.sequences.find(s => s.id === sequenceId)?.name ?? String(sequenceId);
    }

    sequenceStatusFor(sequenceId: number): string {
        return this.sequences.find(s => s.id === sequenceId)?.status ?? '';
    }

    // ─── Recommendations panel (#1071 P8) ──────────────

    async loadRecommendations(): Promise<void> {
        const companyId = this.selectedCompanyId;
        if (!companyId) return;
        this.recommendationsState = { status: 'loading', message: '' };
        this.recActionError = '';
        this.cdr.detectChanges();
        const outcome = await this.actionClient.listRecommendations(companyId);
        if (companyId !== this.selectedCompanyId) return; // stale company
        switch (outcome.kind) {
            case 'success':
                this.recommendations = outcome.data;
                this.recommendationsState = this.recommendations.length > 0
                    ? { status: 'ready', message: '' }
                    : { status: 'empty', message: 'No recommendations for this brand.' };
                break;
            case 'not-deployed':
                this.recommendations = [];
                this.recommendationsState = { status: 'not-deployed', message: outcome.message };
                break;
            case 'error':
                this.recommendations = [];
                this.recommendationsState = { status: 'error', message: outcome.message };
                break;
        }
        this.cdr.detectChanges();
    }

    get openRecommendations(): OutboundRecommendation[] {
        return this.recommendations.filter(r => r.status === 'Open');
    }

    get resolvedRecommendations(): OutboundRecommendation[] {
        return this.recommendations.filter(r => r.status !== 'Open');
    }

    async applyRecommendation(rec: OutboundRecommendation): Promise<void> {
        if (this.recBusyId || !rec.actionable) return;
        this.recBusyId = rec.id;
        this.recActionError = '';
        this.cdr.detectChanges();
        const outcome = await this.actionClient.applyRecommendation(rec.id, this.selectedCompanyId);
        this.recBusyId = null;
        switch (outcome.kind) {
            case 'success':
                // Apply (pause/scale) may report Applied=false for advisory ops;
                // surface its message, then refresh the panel either way.
                if (!outcome.data.applied && outcome.data.message) {
                    this.recActionError = outcome.data.message;
                }
                await this.loadRecommendations();
                break;
            case 'not-deployed':
                this.recActionError = `Apply action not deployed to this environment: ${outcome.message}`;
                break;
            case 'error':
                this.recActionError = outcome.message;
                LogError(`[OutboundDashboard] applyRecommendation failed: ${outcome.message}`);
                break;
        }
        this.cdr.detectChanges();
    }

    async dismissRecommendation(rec: OutboundRecommendation): Promise<void> {
        if (this.recBusyId) return;
        if (!confirm(`Dismiss recommendation "${rec.title}"?`)) return;
        this.recBusyId = rec.id;
        this.recActionError = '';
        this.cdr.detectChanges();
        const outcome = await this.actionClient.dismissRecommendation(rec.id, this.selectedCompanyId);
        this.recBusyId = null;
        switch (outcome.kind) {
            case 'success':
                await this.loadRecommendations();
                break;
            case 'not-deployed':
                this.recActionError = `Dismiss action not deployed to this environment: ${outcome.message}`;
                break;
            case 'error':
                this.recActionError = outcome.message;
                LogError(`[OutboundDashboard] dismissRecommendation failed: ${outcome.message}`);
                break;
        }
        this.cdr.detectChanges();
    }

    // ─── Variant rollup sub-section (#1071 P8) ──────────

    toggleRollup(): void {
        this.rollupExpanded = !this.rollupExpanded;
        if (this.rollupExpanded && !this.variantRollup && this.rollupState.status !== 'loading') {
            void this.loadVariantRollup();
        }
        this.cdr.detectChanges();
    }

    private async loadVariantRollup(): Promise<void> {
        if (!this.selectedCompanyId || !this.selectedSequence) return;
        const companyId = this.selectedCompanyId;
        const seqId = this.selectedSequence.id;
        this.rollupState = { status: 'loading', message: '' };
        this.cdr.detectChanges();
        const outcome = await this.actionClient.getVariantRollup(companyId, seqId);
        // Ignore a stale response if the user navigated away.
        if (this.selectedCompanyId !== companyId || this.selectedSequence?.id !== seqId) return;
        switch (outcome.kind) {
            case 'success':
                this.variantRollup = outcome.data;
                this.rollupState = outcome.data.variants.length > 0
                    ? { status: 'ready', message: '' }
                    : { status: 'empty', message: 'No per-variant rollup data yet (no GA4 click signal on this sequence).' };
                break;
            case 'not-deployed':
                this.rollupState = { status: 'not-deployed', message: outcome.message };
                break;
            case 'error':
                // GA4 not connected for this brand is an expected, non-error state
                // (the outbound brands have no GA4 yet) — surface it as calm info,
                // not a red failure, so the panel doesn't read as broken.
                this.rollupState = /ga4 integration/i.test(outcome.message)
                    ? { status: 'empty', message: 'GA4 isn’t connected for this brand yet — per-variant click→meeting attribution appears once it is. (Reply reply-rate is step-level; the agent still uses it for keep/iterate/kill.)' }
                    : { status: 'error', message: outcome.message };
                break;
        }
        this.cdr.detectChanges();
    }

    /** The step-level note (shared across rows) — surfaced once above the table. */
    get rollupStepLevelNote(): string {
        return this.variantRollup?.variants.find(v => v.note.length > 0)?.note ?? '';
    }

    // ─── Central Pool panel (#1071 P8) ──────────────────

    async loadPool(): Promise<void> {
        const companyId = this.selectedCompanyId;
        if (!companyId) return;
        this.poolState = { status: 'loading', message: '' };
        this.cdr.detectChanges();
        const outcome = await this.actionClient.listOutboundMeetings(companyId);
        if (companyId !== this.selectedCompanyId) return; // stale company
        switch (outcome.kind) {
            case 'success':
                this.poolMeetings = outcome.data;
                this.poolState = this.poolMeetings.length > 0
                    ? { status: 'ready', message: '' }
                    : { status: 'empty', message: 'No meetings in the central pool for this brand yet.' };
                break;
            case 'not-deployed':
                this.poolMeetings = [];
                this.poolState = { status: 'not-deployed', message: outcome.message };
                break;
            case 'error':
                this.poolMeetings = [];
                this.poolState = { status: 'error', message: outcome.message };
                break;
        }
        this.cdr.detectChanges();
    }

    /** Distinct SourceBrand values for the filter dropdown. */
    get poolBrands(): string[] {
        const set = new Set<string>();
        for (const m of this.poolMeetings) {
            if (m.sourceBrand) set.add(m.sourceBrand);
        }
        return Array.from(set).sort((a, b) => a.localeCompare(b));
    }

    get filteredPoolMeetings(): OutboundPoolMeeting[] {
        if (!this.poolBrandFilter) return this.poolMeetings;
        return this.poolMeetings.filter(m => m.sourceBrand === this.poolBrandFilter);
    }

    async ingestMeetings(): Promise<void> {
        if (!this.selectedCompanyId || this.isIngesting) return;
        this.isIngesting = true;
        this.ingestError = '';
        this.ingestResult = null;
        this.cdr.detectChanges();
        const outcome = await this.actionClient.ingestOutboundMeetings(this.selectedCompanyId);
        this.isIngesting = false;
        switch (outcome.kind) {
            case 'success':
                this.ingestResult = outcome.data;
                await this.loadPool();
                break;
            case 'not-deployed':
                this.ingestError = `Ingest action not deployed to this environment: ${outcome.message}`;
                break;
            case 'error':
                this.ingestError = outcome.message;
                LogError(`[OutboundDashboard] ingestMeetings failed: ${outcome.message}`);
                break;
        }
        this.cdr.detectChanges();
    }

    toggleStepSelection(index: number): void {
        this.selectedStepIndex = this.selectedStepIndex === index ? null : index;
    }

    getPerfForStep(index: number): OutboundStepPerf | null {
        return this.sequenceDetail?.perf[index] ?? null;
    }

    getVariantLetter(index: number): string {
        return String.fromCharCode(65 + index);
    }

    // ─── Inline per-variant copy editing ───────────────
    // SAFETY: every path below routes through actionClient.updateSequenceVariant,
    // which only edits copy in place. There is no start/unpause affordance.

    private variantKey(stepIndex: number, variantIndex: number): string {
        return `${stepIndex}:${variantIndex}`;
    }

    isEditingVariant(stepIndex: number, variantIndex: number): boolean {
        return this.editingVariantKey === this.variantKey(stepIndex, variantIndex);
    }

    /** A variant can only be edited in place if Reply gave us both its step id and variant id. */
    canEditVariant(variant: OutboundStepVariant): boolean {
        return variant.stepId !== null && variant.variantId !== null;
    }

    openVariantEdit(stepIndex: number, variantIndex: number, variant: OutboundStepVariant): void {
        if (this.isSavingVariant) return;
        this.editingVariantKey = this.variantKey(stepIndex, variantIndex);
        this.variantEditSubject = variant.subject;
        this.variantEditBody = variant.body;
        this.variantEditError = '';
        this.cdr.detectChanges();
    }

    closeVariantEdit(): void {
        if (this.isSavingVariant) return;
        this.editingVariantKey = null;
        this.variantEditError = '';
        this.cdr.detectChanges();
    }

    async saveVariantEdit(stepIndex: number, variantIndex: number, variant: OutboundStepVariant): Promise<void> {
        if (this.isSavingVariant) return;
        if (!this.selectedCompanyId || !this.selectedSequence) {
            this.variantEditError = 'No company or sequence selected.';
            return;
        }
        if (variant.stepId === null || variant.variantId === null) {
            this.variantEditError = 'Reply did not return a step/variant id for this variant — it cannot be edited in place.';
            return;
        }
        this.isSavingVariant = true;
        this.variantEditError = '';
        this.cdr.detectChanges();

        const outcome = await this.actionClient.updateSequenceVariant(
            this.selectedCompanyId,
            this.selectedSequence.id,
            variant.stepId,
            variant.variantId,
            this.variantEditSubject,
            this.variantEditBody,
        );
        this.isSavingVariant = false;

        switch (outcome.kind) {
            case 'success': {
                // Optimistically reflect the saved copy, then refresh from Reply.
                const liveVariant = this.sequenceDetail?.steps[stepIndex]?.variants[variantIndex];
                if (liveVariant) {
                    liveVariant.subject = this.variantEditSubject;
                    liveVariant.body = this.variantEditBody;
                }
                this.editingVariantKey = null;
                this.cdr.detectChanges();
                this.reloadSequenceDetail();
                break;
            }
            case 'not-deployed':
                this.variantEditError = `Update Sequence Variant action not deployed to this environment: ${outcome.message}`;
                break;
            case 'error':
                this.variantEditError = outcome.message;
                LogError(`[OutboundDashboard] saveVariantEdit failed: ${outcome.message}`);
                break;
        }
        this.cdr.detectChanges();
    }

    /** Re-fetch the open sequence's detail (after a variant edit) without collapsing the panel. */
    private reloadSequenceDetail(): void {
        if (!this.selectedCompanyId || !this.selectedSequence) return;
        const seqId = this.selectedSequence.id;
        void this.actionClient.getSequenceDetail(this.selectedCompanyId, seqId).then(outcome => {
            // Ignore a stale response if the user navigated away.
            if (this.selectedSequence?.id !== seqId) return;
            if (outcome.kind === 'success') {
                this.sequenceDetail = outcome.data;
            } else if (outcome.kind === 'error') {
                LogError(`[OutboundDashboard] reloadSequenceDetail failed: ${outcome.message}`);
            }
            this.cdr.detectChanges();
        });
    }

    // ─── Action invocation ─────────────────────────────
    private async loadOutboundData(): Promise<void> {
        const companyId = this.selectedCompanyId;
        if (!companyId) return;
        const token = ++this.loadToken;

        this.statsState = { status: 'loading', message: '' };
        this.sequencesState = { status: 'loading', message: '' };
        this.stats = null;
        this.sequences = [];
        this.cdr.detectChanges();

        const [statsOutcome, sequencesOutcome] = await Promise.all([
            this.actionClient.getReplyStats(companyId),
            this.actionClient.getReplySequences(companyId),
        ]);
        if (token !== this.loadToken) return;

        switch (statsOutcome.kind) {
            case 'success':
                this.stats = statsOutcome.data;
                this.statsState = { status: 'ready', message: '' };
                break;
            case 'not-deployed':
                this.statsState = { status: 'not-deployed', message: statsOutcome.message };
                break;
            case 'error':
                this.statsState = { status: 'error', message: statsOutcome.message };
                break;
        }

        switch (sequencesOutcome.kind) {
            case 'success':
                this.sequences = sequencesOutcome.data;
                this.sequencesState = this.sequences.length > 0
                    ? { status: 'ready', message: '' }
                    : { status: 'empty', message: 'No sequences found for this brand.' };
                break;
            case 'not-deployed':
                this.sequencesState = { status: 'not-deployed', message: sequencesOutcome.message };
                break;
            case 'error':
                this.sequencesState = { status: 'error', message: sequencesOutcome.message };
                break;
        }

        this.cdr.detectChanges();
    }
}

/**
 * Anti-tree-shake hook so MJ Explorer can `import('outbound-ui').then(m => m.LoadOutboundUI())`
 * without the OutboundDashboard @RegisterClass registration being dropped by the bundler.
 */
export function LoadOutboundDashboard(): void { /* keep registration alive */ }

// Re-export types used in the template via the component's module boundary
export type { OutboundStepPerf, OutboundSequenceStep };
